/**
 * Aimepic OneCare Branding Configuration
 * 
 * Central source of truth for all branding elements across the platform.
 * 
 * Branding Strategy:
 * - Company: Aimepic (parent brand)
 * - Product: OneCare (platform product)
 * - Full Name: Aimepic OneCare
 * 
 * Usage:
 * - Import these constants instead of hardcoding strings
 * - Ensures consistent branding across all components
 */

export const BRANDING = {
  // Company Information
  company: {
    name: 'Aimepic',
    fullName: 'Aimepic',
    description: 'Secure, intelligent software for work and life',
  },

  // Product Information
  product: {
    name: 'OneCare',
    fullName: 'Aimepic OneCare',
    tagline: 'Healthcare Platform',
    description: 'Unified platform for healthcare operations, patient management, and clinical workflows',
    shortDescription: 'Healthcare Operations Platform',
  },

  // Display Variations
  display: {
    // For page titles and headers
    primary: 'OneCare',
    // For login, landing, and marketing
    full: 'Aimepic OneCare',
    // For copyright and legal
    legal: 'Aimepic',
    // For mobile app
    mobile: {
      appName: 'OneCare',
      line1: 'Aimepic OneCare',
      line2: 'Healthcare Platform',
    },
  },

  // Page Titles (suffix format)
  pageTitles: {
    // Public pages
    landing: 'Aimepic OneCare - Healthcare Operations Platform',
    login: 'Sign In - OneCare',
    loginLegacy: 'Sign In - OneCare',
    
    // Internal pages (authenticated)
    dashboard: 'Dashboard - OneCare',
    communicator: 'Messages - OneCare',
    messages: 'Conversations - OneCare',
    settings: 'Settings - OneCare',
    admin: 'Administration - OneCare',
    platformAdmin: 'Platform Administration - OneCare',
    patients: 'Patient Management - OneCare',
    staff: 'Staff Dashboard - OneCare',
    aiChat: 'AI Assistant - OneCare',
    notifications: 'Notifications - OneCare',
  },

  // Taglines & Slogans
  taglines: {
    primary: 'Healthcare Platform',
    marketing: 'Unified Healthcare Management Platform',
    short: 'Healthcare Operations Platform',
  },

  // Contact & Social
  contact: {
    email: 'contact-us@aimepic.com',
    salesEmail: 'contact-us@aimepic.com',
    website: 'https://aimepic.com',
    supportUrl: 'https://support.aimepic.com',
  },

  // Branding Colors (default theme)
  colors: {
    primary: '#2563eb',    // Blue
    secondary: '#059669',  // Green
    accent: '#dc2626',     // Red
  },

  // Copyright
  copyright: {
    year: new Date().getFullYear(),
    text: `© ${new Date().getFullYear()} Aimepic. All rights reserved.`,
    shortText: `© ${new Date().getFullYear()} Aimepic`,
  },

  // SEO & Meta
  meta: {
    defaultTitle: 'Aimepic OneCare - Healthcare Operations Platform',
    defaultDescription: 'Comprehensive healthcare operations and management platform for hospitals, clinics, and healthcare facilities. Streamline patient care, staff coordination, and clinical workflows.',
    keywords: 'healthcare management, hospital operations, patient care platform, clinical workflows, healthcare technology, OneCare, Aimepic',
  },
} as const;

// Export individual sections for convenience
export const { company, product, display, pageTitles, taglines, contact, colors, copyright, meta } = BRANDING;

/**
 * Cache-busting version for brand artwork served from `public/`.
 *
 * Those filenames are stable across deploys, and nginx serves them
 * `Cache-Control: public, max-age=31536000, immutable`. Replacing the file on
 * the server therefore never reaches a browser — or Cloudflare — that already
 * holds a copy; `immutable` means even a normal reload will not revalidate.
 * Appending `?v=` makes each revision a distinct URL, which is the only thing
 * that reliably invalidates it.
 *
 * Bump this whenever the artwork changes. `ICON_VERSION` in
 * hooks/useBrandIcons.ts does the same job for the tab / home-screen icons.
 */
export const ASSET_VERSION = '2';

/** The OneCare mark, as shown in-page (login, sidebar, topbar, landing). */
export const ONECARE_LOGO = `/onecare-transparent.png?v=${ASSET_VERSION}`;

// Helper functions
export const getPageTitle = (page: keyof typeof pageTitles): string => {
  return pageTitles[page];
};

export const getFullBrandName = (): string => {
  return product.fullName;
};

export const getProductName = (): string => {
  return product.name;
};

export const getCompanyName = (): string => {
  return company.name;
};

export const getCopyrightText = (): string => {
  return copyright.text;
};

// Default export
export default BRANDING;

